import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import { OrderCompletionState, OrderConfirmationGuestCheckoutParams, OrderConfirmationParams, PaymentOrderDetails } from "./order-completion.interface";
import environment, { isGuestCheckoutPage } from "../../common/environment";
import { StoreState } from "../../store/store.interface";
import { ActionStatus } from "../../common/constants/action-status";
import { defaultApiState } from "../../common/constants/api-constants";
import { getResponseData } from "../../common/utils/api-utils";
import { ShopApi } from "../../common/constants/shop-api";
import { mapOrderDetailsMagentoResponse } from "./payment-order-details.mapping";
import { getCustomerToken, loadCustomer } from "../customer/customer";
import store from "../../store/store";
import { GuestCheckoutAPI, getCartIdFromURL } from "../../common/constants/guest-checkout-api";

/*----- DECLARE THUNKS ------*/

export const getOrderData = createAsyncThunk('orderCompletion/getOrderData', async ({ orderId }: OrderConfirmationParams, thunkAPI) => {
    try {
        if (orderId) {
            let paymentOrderDetails: PaymentOrderDetails;
            if (isGuestCheckoutPage()) {
                paymentOrderDetails = await getOrderDataMagentoApiCallGuestCheckout({ cartId: getCartIdFromURL(), orderId });
            } else {
                await thunkAPI.dispatch(loadCustomer());
                paymentOrderDetails = await getOrderDataMagentoApiCall({ orderId });
            }
            return {
                ...paymentOrderDetails,
                orderId
            };
        } else {
            return thunkAPI.rejectWithValue({
                message: 'Missing order ID.',
                orderId: orderId
            });
        }
    } catch (error) {
        return thunkAPI.rejectWithValue({
            error: error,
            orderId: orderId
        })
    }
}, {
    condition: ((_args: OrderConfirmationParams, { getState }) => {
        const state = getState() as StoreState;
        return ![ActionStatus.loading].includes(state.orderCompletionState.status);
    })
});

/*----- CREATE INITIAL STATE ------*/

export const orderCompletionInitialState: OrderCompletionState = defaultApiState;

/*----- CREATE STORE SLICE ------*/

export const orderCompletionSlice = createSlice({
    name: 'orderCompletion',
    initialState: orderCompletionInitialState,
    reducers: {
        updateOrderCompletionStatus(state, action) {
            state.status = ActionStatus[action.payload]
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getOrderData.pending, (state) => {
                state.status = ActionStatus.loading;
            })
            .addCase(getOrderData.fulfilled, (state, action) => {
                state.status = ActionStatus.succeeded;
                state.data = action.payload;
                state.error = undefined;
            })
            .addCase(getOrderData.rejected, (state, action: any) => {
                state.status = ActionStatus.failed;
                state.error = action.payload.error;
                state.data = {
                    id: undefined
                }
            })
    }
});

/*----- CREATE SLICE ACTIONS ------*/

export const { updateOrderCompletionStatus } = orderCompletionSlice.actions;

/*----- CREATE REDUCER ------*/

export const orderCompletionReducer = orderCompletionSlice.reducer;

/*----- API CALLS ------*/

async function getOrderDataMagentoApiCallGuestCheckout({ cartId, orderId }: OrderConfirmationGuestCheckoutParams): Promise<PaymentOrderDetails> {
    const url = new URL(environment().magentoUrl + GuestCheckoutAPI.orderConfirmation(cartId, orderId));

    return mapOrderDetailsMagentoResponse(await getResponseData(
        new Request(url.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        })
    ));
}

async function getOrderDataMagentoApiCall({ orderId }: OrderConfirmationParams): Promise<PaymentOrderDetails> {
    const url = new URL(environment().magentoUrl + ShopApi.orderConfirmationDataMagento.replace(":orderId", orderId!));
    const token = getCustomerToken(store.getState() as StoreState);

    return mapOrderDetailsMagentoResponse(await getResponseData(
        new Request(url.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        })
    ));
}